'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useUser } from '@/components/providers/user-provider'
import { LikeStatus } from '@/enums/const'
import { useCreditRewardStore } from '@/hooks/use-credit-reward'
import type { CreationDetail } from '@/lib/api/models'

import { likeVideo, unlikeVideo } from '../../services'

import { CREATION_DETAIL_KEY } from '../creation/use-creation-detail'
import { EXPLORE_LIST_KEY, FILTER_EXPLORE_LIST_KEY } from '../explore/use-explore'

// 更新所有相关查询中的作品数据
const updateWorkInAllQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string,
  updater: (work: CreationDetail) => CreationDetail
) => {
  // 更新单个作品详情查询
  queryClient.setQueryData<CreationDetail>([CREATION_DETAIL_KEY, jobId], oldData => {
    return oldData ? updater(oldData) : oldData
  })

  // 更新explore列表查询
  queryClient.setQueriesData({ queryKey: [EXPLORE_LIST_KEY] }, (oldData: any) => {
    if (!oldData?.pages) return oldData

    return {
      ...oldData,
      pages: oldData.pages.map((page: any) => ({
        ...page,
        list: page.list.map((item: CreationDetail) => (item.jobId === jobId ? updater(item) : item))
      }))
    }
  })

  // 更新筛选explore列表查询
  queryClient.setQueriesData({ queryKey: [FILTER_EXPLORE_LIST_KEY] }, (oldData: any) => {
    if (!oldData?.pages) return oldData

    return {
      ...oldData,
      pages: oldData.pages.map((page: any) => ({
        ...page,
        list: page.list.map((item: CreationDetail) => (item.jobId === jobId ? updater(item) : item))
      }))
    }
  })
}

export const useLikeWork = (jobId: string) => {
  const queryClient = useQueryClient()
  const showReward = useCreditRewardStore(state => state.showReward)
  const { subscription } = useUser()

  return useMutation({
    mutationFn: likeVideo,
    onMutate: async () => {
      // 取消正在进行的查询以避免冲突
      await queryClient.cancelQueries({ queryKey: [CREATION_DETAIL_KEY, jobId] })
      await queryClient.cancelQueries({ queryKey: [EXPLORE_LIST_KEY] })
      await queryClient.cancelQueries({ queryKey: [FILTER_EXPLORE_LIST_KEY] })

      // 获取当前数据的快照用于回滚
      const previousWorkData = queryClient.getQueryData<CreationDetail>([
        CREATION_DETAIL_KEY,
        jobId
      ])
      const previousExploreData = queryClient.getQueriesData({ queryKey: [EXPLORE_LIST_KEY] })
      const previousFilterData = queryClient.getQueriesData({ queryKey: [FILTER_EXPLORE_LIST_KEY] })

      // 乐观更新：立即更新所有相关查询中的点赞状态
      updateWorkInAllQueries(queryClient, jobId, work => ({
        ...work,
        isLike: LikeStatus.LIKE,
        likeCount: work.likeCount + 1,
        // cover 是 getter，展开时不会复制，需要手动添加
        cover: work.cover
      }))

      // 返回上下文对象，包含之前的数据用于回滚
      return {
        previousWorkData,
        previousExploreData,
        previousFilterData
      }
    },
    onSuccess: response => {
      // 如果有积分奖励，显示奖励弹窗并刷新积分
      if (response?.credits && response.credits > 0) {
        showReward(response.credits, 'like')
        subscription.refresh()
      }
    },
    onError: (error, variables, context) => {
      // 发生错误时回滚到之前的状态
      if (context?.previousWorkData) {
        queryClient.setQueryData([CREATION_DETAIL_KEY, jobId], context.previousWorkData)
      }
      if (context?.previousExploreData) {
        context.previousExploreData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      if (context?.previousFilterData) {
        context.previousFilterData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('likeWork error', error)
    }
  })
}

export const useUnlikeWork = (jobId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: unlikeVideo,
    onMutate: async () => {
      // 取消正在进行的查询以避免冲突
      await queryClient.cancelQueries({ queryKey: [CREATION_DETAIL_KEY, jobId] })
      await queryClient.cancelQueries({ queryKey: [EXPLORE_LIST_KEY] })
      await queryClient.cancelQueries({ queryKey: [FILTER_EXPLORE_LIST_KEY] })

      // 获取当前数据的快照用于回滚
      const previousWorkData = queryClient.getQueryData<CreationDetail>([
        CREATION_DETAIL_KEY,
        jobId
      ])
      const previousExploreData = queryClient.getQueriesData({ queryKey: [EXPLORE_LIST_KEY] })
      const previousFilterData = queryClient.getQueriesData({ queryKey: [FILTER_EXPLORE_LIST_KEY] })

      // 乐观更新：立即更新所有相关查询中的点赞状态
      updateWorkInAllQueries(queryClient, jobId, work => ({
        ...work,
        isLike: LikeStatus.UNLIKE,
        likeCount: Math.max(0, work.likeCount - 1), // 确保不会小于0
        // cover 是 getter，展开时不会复制，需要手动添加
        cover: work.cover
      }))

      // 返回上下文对象，包含之前的数据用于回滚
      return {
        previousWorkData,
        previousExploreData,
        previousFilterData
      }
    },
    onError: (error, variables, context) => {
      // 发生错误时回滚到之前的状态
      if (context?.previousWorkData) {
        queryClient.setQueryData([CREATION_DETAIL_KEY, jobId], context.previousWorkData)
      }
      if (context?.previousExploreData) {
        context.previousExploreData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      if (context?.previousFilterData) {
        context.previousFilterData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('unlikeWork error', error)
    },
    onSettled: () => {
      // 成功后不需要重新获取数据，因为乐观更新已经是最新状态
      // 只在出错时才需要重新获取以确保数据一致性
    }
  })
}
